/**
 * Widget catalogues of the accounting, firm and stock dashboards, and the
 * resolution of what a reader sees on each.
 *
 * Each dashboard has one catalogue. A reader sees the catalogue's widgets
 * narrowed by the plan, the permission and the company's disabled modules,
 * then narrowed and reordered by the company default and by the reader's
 * own choice. Every layer after the permission only removes or reorders;
 * none adds a widget.
 */
function catalogue(widgets) {
    return Object.fromEntries(widgets.map((widget) => [widget.key, widget]));
}
/** `/accounting`: the four KPI cards, then the entries table and the side cards. */
export const ACCOUNTING_DASHBOARD_WIDGETS = catalogue([
    { key: "kpi-result", permission: "accounting:read", planFeature: "accounting" },
    { key: "kpi-treasury", permission: "accounting:read", planFeature: "accounting" },
    { key: "kpi-receivables", permission: "accounting:read", planFeature: "accounting" },
    { key: "kpi-vat", permission: "accounting:read", planFeature: "accounting" },
    { key: "recent-entries", permission: "accounting:read", planFeature: "accounting" },
    { key: "month-activity", permission: "accounting:read", planFeature: "accounting" },
    { key: "income-expense", permission: "accounting:read", planFeature: "accounting" },
    { key: "budget-alerts", permission: "accounting:read", planFeature: "accounting" },
]);
/** `/cabinet/dossiers`: the blocks of each client file's card. */
export const FIRM_DASHBOARD_WIDGETS = catalogue([
    { key: "dossier-indicators", permission: "cabinet:access" },
    { key: "last-entry", permission: "cabinet:access" },
    { key: "team", permission: "cabinet:access" },
    { key: "dsf-visa", permission: "cabinet:access" },
]);
/** `/stock`: the four KPI cards and the two tables. */
export const STOCK_DASHBOARD_WIDGETS = catalogue([
    { key: "quick-actions", permission: "stock:read", planFeature: "stock" },
    { key: "kpi-articles", permission: "stock:read", planFeature: "stock" },
    { key: "kpi-low-stock", permission: "stock:read", planFeature: "stock" },
    { key: "kpi-expiring", permission: "stock:read", planFeature: "stock" },
    { key: "kpi-value", permission: "stock:read", planFeature: "stock" },
    { key: "low-stock-table", permission: "stock:read", planFeature: "stock" },
    { key: "expiring-table", permission: "stock:read", planFeature: "stock" },
]);
export const DASHBOARD_CATALOGUES = {
    accounting: ACCOUNTING_DASHBOARD_WIDGETS,
    firm: FIRM_DASHBOARD_WIDGETS,
    stock: STOCK_DASHBOARD_WIDGETS,
};
/** The `UiPreference` key of each dashboard's company default and user choice. */
export const DASHBOARD_CATALOGUE_PREFERENCE_KEYS = {
    accounting: "dashboard.accounting.widgets",
    firm: "dashboard.firm.widgets",
    stock: "dashboard.stock.widgets",
};
/** The catalogue a preference key belongs to; undefined for any other key. */
export function catalogueOfPreferenceKey(key) {
    return Object.keys(DASHBOARD_CATALOGUE_PREFERENCE_KEYS).find((id) => DASHBOARD_CATALOGUE_PREFERENCE_KEYS[id] === key);
}
/** The catalogue's keys, in catalogue order. */
export function catalogueKeys(id) {
    return Object.keys(DASHBOARD_CATALOGUES[id]);
}
/**
 * A stored or submitted selection as a list of the catalogue's keys:
 * unknown keys and repeats dropped, order kept. Anything but an array is
 * empty.
 */
export function sanitizeCatalogueSelection(id, raw) {
    if (!Array.isArray(raw))
        return [];
    const catalog = DASHBOARD_CATALOGUES[id];
    const kept = [];
    for (const key of raw) {
        if (typeof key === "string" && Object.hasOwn(catalog, key) && !kept.includes(key))
            kept.push(key);
    }
    return kept;
}
/** What a reader may see at all: plan, permission and company modules applied to the catalogue. */
export function availableCatalogueWidgets(id, input) {
    return catalogueKeys(id).filter((key) => {
        const { planFeature, permission } = DASHBOARD_CATALOGUES[id][key];
        if (planFeature !== undefined && input.planFeatures !== null && !input.planFeatures.includes(planFeature)) {
            return false;
        }
        if (permission !== null && !input.can(permission))
            return false;
        return input.isModuleDisabled?.(key) !== true;
    });
}
/**
 * `selection` applied to `allowed`: only the keys of `allowed` it names, in
 * its order. A selection that is empty, or names none of `allowed`, leaves
 * `allowed` as it is, so a stale choice never blanks the page.
 */
export function narrowCatalogueWidgets(id, allowed, selection) {
    const chosen = sanitizeCatalogueSelection(id, selection).filter((key) => allowed.includes(key));
    return chosen.length > 0 ? chosen : [...allowed];
}
export function resolveCatalogueWidgets(id, input) {
    const allowed = availableCatalogueWidgets(id, input);
    return narrowCatalogueWidgets(id, narrowCatalogueWidgets(id, allowed, input.company), input.user);
}
//# sourceMappingURL=dashboard-catalogues.js.map